import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  FileText,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import vendorInvoiceService from '../../services/vendorInvoiceService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const VendorInvoice = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalInvoices: 0,
    unpaidInvoices: 0,
    partiallyPaidInvoices: 0,
    paidInvoices: 0,
    overdueInvoices: 0,
    totalAmount: 0,
    totalOutstanding: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [invoicesResponse, statsResponse] = await Promise.all([
        vendorInvoiceService.getAllVendorInvoices(),
        vendorInvoiceService.getVendorInvoiceStats()
      ])
      
      if (invoicesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (invoicesResponse.data || []).map(invoice => ({
          ...invoice,
          id: invoice._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (invoicesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No vendor invoice records found. Create your first vendor invoice!')
        }
      } else {
        toast.error(invoicesResponse.message || 'Failed to load vendor invoices')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading vendor invoice records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view vendor invoices')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load vendor invoice records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getPaymentStatusBadgeClass = (status) => {
    const statusMap = {
      'Unpaid': 'danger',
      'Partially Paid': 'warning',
      'Paid': 'active',
      'Overdue': 'danger',
      'Cancelled': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'invoiceNumber',
      header: 'Invoice #',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'supplierInvoiceNumber',
      header: 'Supplier Invoice #',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'invoiceDate',
      header: 'Invoice Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'purchaseOrderNumber',
      header: 'PO Number',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'financialSummary.totalAmount',
      header: 'Total Amount',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(row.original.financialSummary?.totalAmount || 0, row.original.financialSummary?.currency || 'INR')}
        </div>
      ),
    },
    {
      accessorKey: 'outstandingAmount',
      header: 'Outstanding',
      cell: ({ getValue, row }) => {
        const amount = getValue() || 0
        return (
          <div style={{ textAlign: 'right', fontWeight: '600', color: amount > 0 ? '#dc3545' : '#28a745' }}>
            {formatCurrency(amount, row.original.financialSummary?.currency || 'INR')}
          </div>
        )
      },
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Payment Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getPaymentStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/procurement/vendor-invoice/form')
  }

  const handleEdit = (invoiceData) => {
    navigate('/procurement/vendor-invoice/edit', { state: { invoiceData } })
  }

  const handleView = (invoiceData) => {
    navigate('/procurement/vendor-invoice/view', { state: { invoiceData } })
  }

  const handleDelete = (invoiceData) => {
    setDeleteItem(invoiceData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting vendor invoice...')
      const response = await vendorInvoiceService.deleteVendorInvoice(deleteItem._id)
      if (response.success) {
        toast.success('Vendor invoice deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete vendor invoice')
      }
    } catch (error) {
      console.error('Error deleting vendor invoice:', error)
      if (error.response?.status === 404) {
        toast.error('Vendor invoice not found')
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Can only delete unpaid invoices')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this vendor invoice')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete vendor invoice. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Transactions', path: '/procurement?tab=transactions' },
    { label: 'Vendor Invoices', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Vendor Invoices</h1>
          <p>Supplier invoices and payment tracking</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Invoice
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalInvoices || 0}</div>
          <div className="metric-title">Total Invoices</div>
          <div className="metric-subtitle">All supplier invoices</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Unpaid</span>
            </div>
          </div>
          <div className="metric-value">{stats.unpaidInvoices || 0}</div>
          <div className="metric-title">Unpaid</div>
          <div className="metric-subtitle">Awaiting payment</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Partial</span>
            </div>
          </div>
          <div className="metric-value">{stats.partiallyPaidInvoices || 0}</div>
          <div className="metric-title">Partially Paid</div>
          <div className="metric-subtitle">Partial settlements</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Paid</span>
            </div>
          </div>
          <div className="metric-value">{stats.paidInvoices || 0}</div>
          <div className="metric-title">Paid</div>
          <div className="metric-subtitle">Fully settled</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Overdue</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdueInvoices || 0}</div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Past due date</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Outstanding</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalOutstanding || 0, 'INR')}</div>
          <div className="metric-title">Total Outstanding</div>
          <div className="metric-subtitle">Amount due</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          className="vendor-invoice-table"
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Vendor Invoice"
        message={`Are you sure you want to delete invoice "${deleteItem?.invoiceNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default VendorInvoice

